import { useState } from "react";
import { useLocation } from "wouter";
import { ArrowRight, Building2, Truck } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { LogoImage } from "../../components/Logo";
import { ThemeToggle } from "../../components/theme-toggle";
import { cn } from "../../lib/utils";

export default function Register() {
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "agency" as "agency" | "fleet",
    companyName: "",
    region: "taiwan" as "taiwan" | "hongkong",
    contactPerson: "",
    phone: "",
  });
  const [error, setError] = useState<string | null>(null);

  const reg = trpc.auth.register.useMutation({
    onSuccess: () => {
      refetch();
      setLocation("/");
    },
    onError: (e) => setError(e.message),
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <button
          onClick={() => setLocation("/login")}
          className="transition-opacity hover:opacity-80"
        >
          <LogoImage className="h-8" />
        </button>
        <ThemeToggle />
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10 md:py-16">
        <div className="animate-fade-up space-y-8">
          <div className="space-y-2">
            <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-primary">
              {t({ "zh-TW": "Step 1 of 1", "en-US": "Step 1 of 1", "zh-CN": "Step 1 of 1" })}
            </p>
            <h1 className="font-display text-4xl font-medium tracking-tight">
              {t({ "zh-TW": "建立合作方帳號", "en-US": "Create your partner account", "zh-CN": "建立合作方帐号" })}
            </h1>
            <p className="text-[13px] text-muted-foreground">
              {t({
                "zh-TW": "註冊後立刻進入工作台。如需新增調度員或管理員帳號，請聯絡平台。",
                "en-US": "You'll land on your dashboard right after signup. Contact us for dispatcher / admin accounts.",
                "zh-CN": "注册后立即进入工作台。如需新增调度员或管理员帐号，请联系平台。",
              })}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              reg.mutate(form);
            }}
            className="space-y-8"
          >
            {/* role selector — visual cards */}
            <section className="space-y-3">
              <Label className="text-[12px]">
                {t({ "zh-TW": "你是…", "en-US": "I am a…", "zh-CN": "你是…" })}
              </Label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  {
                    value: "agency" as const,
                    Icon: Building2,
                    title: t({ "zh-TW": "需求方", "en-US": "Agency", "zh-CN": "需求方" }),
                    desc: t({
                      "zh-TW": "旅行社、訂車的一方",
                      "en-US": "Travel agency placing requests",
                      "zh-CN": "旅行社、订车的一方",
                    }),
                  },
                  {
                    value: "fleet" as const,
                    Icon: Truck,
                    title: t({ "zh-TW": "供應方", "en-US": "Fleet", "zh-CN": "供应方" }),
                    desc: t({
                      "zh-TW": "車隊、提供車輛服務的一方",
                      "en-US": "Fleet supplying vehicles",
                      "zh-CN": "车队、提供车辆服务的一方",
                    }),
                  },
                ].map(({ value, Icon, title, desc }) => {
                  const active = form.role === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => set("role", value)}
                      className={cn(
                        "group relative flex items-start gap-3 rounded-lg border p-4 text-left transition-all",
                        active
                          ? "border-primary bg-primary-soft shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-primary)_12%,transparent)]"
                          : "border-border bg-card hover:border-border-strong",
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5 shrink-0",
                          active ? "text-primary-soft-foreground" : "text-muted-foreground",
                        )}
                        strokeWidth={1.75}
                      />
                      <div className="space-y-0.5">
                        <p className={cn("font-display font-medium leading-tight", active && "text-primary-soft-foreground")}>
                          {title}
                        </p>
                        <p className={cn("text-[12px]", active ? "text-primary-soft-foreground/80" : "text-muted-foreground")}>
                          {desc}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="space-y-4">
              <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
                {t({ "zh-TW": "公司資訊", "en-US": "Company", "zh-CN": "公司信息" })}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[12px]">{t({ "zh-TW": "公司名稱", "en-US": "Company name", "zh-CN": "公司名称" })}</Label>
                  <Input required value={form.companyName} onChange={(e) => set("companyName", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</Label>
                  <Select value={form.region} onChange={(e) => set("region", e.target.value as "taiwan" | "hongkong")}>
                    <option value="taiwan">{t({ "zh-TW": "台灣 Taiwan", "en-US": "Taiwan", "zh-CN": "台湾 Taiwan" })}</option>
                    <option value="hongkong">{t({ "zh-TW": "香港 Hong Kong", "en-US": "Hong Kong", "zh-CN": "香港 Hong Kong" })}</option>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">{t({ "zh-TW": "聯絡人", "en-US": "Contact", "zh-CN": "联系人" })}</Label>
                  <Input value={form.contactPerson} onChange={(e) => set("contactPerson", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">{t({ "zh-TW": "電話", "en-US": "Phone", "zh-CN": "电话" })}</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
                {t({ "zh-TW": "帳號", "en-US": "Account", "zh-CN": "帐号" })}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[12px]">{t({ "zh-TW": "你的姓名", "en-US": "Your name", "zh-CN": "你的姓名" })}</Label>
                  <Input required value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Email</Label>
                  <Input type="email" required autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-[12px]">
                    {t({ "zh-TW": "密碼", "en-US": "Password", "zh-CN": "密码" })}
                    <span className="ml-1.5 font-normal text-muted-foreground">
                      {t({ "zh-TW": "（至少 8 碼）", "en-US": "(min 8 chars)", "zh-CN": "（至少 8 位）" })}
                    </span>
                  </Label>
                  <PasswordInput
                    
                    required
                    autoComplete="new-password"
                    minLength={8}
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                  />
                </div>
              </div>
            </section>

            {error && (
              <div className="rounded-md border border-[color-mix(in_oklab,var(--color-destructive)_30%,transparent)] bg-destructive-soft px-3 py-2 text-[12px] text-destructive">
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button type="button" variant="ghost" onClick={() => setLocation("/login")}>
                {t({ "zh-TW": "已有帳號？登入", "en-US": "Already have an account?", "zh-CN": "已有帐号？登入" })}
              </Button>
              <Button type="submit" size="lg" disabled={reg.isPending}>
                {reg.isPending
                  ? t({ "zh-TW": "建立中…", "en-US": "Creating…", "zh-CN": "建立中…" })
                  : t({ "zh-TW": "建立帳號並開始", "en-US": "Create account & continue", "zh-CN": "建立帐号并开始" })}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
